import React, { useState, useCallback, useMemo } from 'react';
import { Download, FileText, Filter, RefreshCw, Shield, AlertTriangle, CheckCircle, Clock, Activity, ChevronDown, ChevronUp, Eye, X, Search, Users, Layers, Calendar } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatPrice = (p) => {
  if (!p) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 })?.format(p);
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })?.format(new Date(d));
};

const formatDateShort = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' })?.format(new Date(d));
};

const escapeCSV = (v) => {
  if (v == null) return '';
  const s = String(v);
  if (s?.includes(',') || s?.includes('"') || s?.includes('\n')) return `"${s?.replace(/"/g, '""')}"`;
  return s;
};

// ─── Fetch all audit data ─────────────────────────────────────────────────────

const fetchAuditData = async (filters) => {
  const { dateFrom, dateTo, types } = filters;

  const applyDateFilter = (q) => {
    if (dateFrom) q = q?.gte('created_at', dateFrom);
    if (dateTo) q = q?.lte('created_at', dateTo + 'T23:59:59');
    return q;
  };

  const results = await Promise.allSettled([
    (types?.includes('transactions') || types?.includes('all'))
      ? applyDateFilter(supabase?.from('escrow_transactions')?.select('id, title, item_title, amount_total, total_amount, status, paystack_reference, reference, created_at, updated_at')?.order('created_at', { ascending: false })?.limit(500))
      : Promise.resolve({ data: [] }),
    (types?.includes('transactions') || types?.includes('all'))
      ? applyDateFilter(supabase?.from('payments')?.select('id, amount, status, paystack_reference, session_id, created_at, updated_at')?.order('created_at', { ascending: false })?.limit(500))
      : Promise.resolve({ data: [] }),
    (types?.includes('disputes') || types?.includes('all'))
      ? applyDateFilter(supabase?.from('escrow_disputes')?.select('id, dispute_reason, status, resolution_notes, created_at, updated_at')?.order('created_at', { ascending: false })?.limit(200))
      : Promise.resolve({ data: [] }),
    (types?.includes('admin_actions') || types?.includes('all'))
      ? applyDateFilter(supabase?.from('escrow_action_logs')?.select('id, action_type, admin_user_id, notes, created_at')?.order('created_at', { ascending: false })?.limit(500))
      : Promise.resolve({ data: [] }),
    (types?.includes('webhooks') || types?.includes('all'))
      ? applyDateFilter(supabase?.from('paystack_webhook_events')?.select('id, event_type, reference, processed, created_at')?.order('created_at', { ascending: false })?.limit(300))
      : Promise.resolve({ data: [] }),
    (types?.includes('commissions') || types?.includes('all'))
      ? applyDateFilter(supabase?.from('commission_payouts')?.select('id, amount, status, commission_type, released_at, created_at')?.order('created_at', { ascending: false })?.limit(200))
      : Promise.resolve({ data: [] }),
    // Digital signatures
    (types?.includes('signatures') || types?.includes('all'))
      ? applyDateFilter(supabase?.from('escrow_digital_signatures')?.select('id, transaction_id, party_role, signer_name, signer_email, signed_at, signature_hash, created_at')?.order('created_at', { ascending: false })?.limit(300))
      : Promise.resolve({ data: [] }),
    // Dispute escalations
    (types?.includes('disputes') || types?.includes('all'))
      ? applyDateFilter(supabase?.from('escrow_dispute_escalations')?.select('id, title, category, severity_level, status, submitted_by_name, submitted_by_role, transaction_ref, amount, created_at')?.order('created_at', { ascending: false })?.limit(200))
      : Promise.resolve({ data: [] }),
  ]);

  return {
    escrow: results?.[0]?.value?.data || [],
    payments: results?.[1]?.value?.data || [],
    disputes: results?.[2]?.value?.data || [],
    adminActions: results?.[3]?.value?.data || [],
    webhooks: results?.[4]?.value?.data || [],
    commissions: results?.[5]?.value?.data || [],
    signatures: results?.[6]?.value?.data || [],
    escalations: results?.[7]?.value?.data || [],
  };
};

// ─── CSV Builder ──────────────────────────────────────────────────────────────

const buildCSV = (data) => {
  const rows = [];
  rows?.push(['Type', 'ID', 'Référence', 'Montant (XOF)', 'Statut', 'Description', 'Utilisateur/Admin', 'Notes/Résolution', 'Horodatage création', 'Horodatage mise à jour']?.map(escapeCSV)?.join(','));
  data?.escrow?.forEach(r => rows?.push(['Transaction Séquestre', r?.id, r?.paystack_reference || r?.reference || '', r?.amount_total || r?.total_amount || '', r?.status, r?.title || r?.item_title || '', '', '', r?.created_at, r?.updated_at]?.map(escapeCSV)?.join(',')));
  data?.payments?.forEach(r => rows?.push(['Paiement', r?.id, r?.paystack_reference || r?.session_id || '', r?.amount || '', r?.status, '', '', '', r?.created_at, r?.updated_at]?.map(escapeCSV)?.join(',')));
  data?.disputes?.forEach(r => rows?.push(['Litige', r?.id, '', '', r?.status, r?.dispute_reason || '', '', r?.resolution_notes || '', r?.created_at, r?.updated_at]?.map(escapeCSV)?.join(',')));
  data?.adminActions?.forEach(r => rows?.push(['Action Admin', r?.id, '', '', r?.action_type || '', '', r?.admin_user_id || '', r?.notes || '', r?.created_at, '']?.map(escapeCSV)?.join(',')));
  data?.webhooks?.forEach(r => rows?.push(['Événement Webhook', r?.id, r?.reference || '', '', r?.processed ? 'Traité' : 'Non traité', r?.event_type || '', '', '', r?.created_at, '']?.map(escapeCSV)?.join(',')));
  data?.commissions?.forEach(r => rows?.push(['Commission', r?.id, '', r?.amount || '', r?.status, r?.commission_type || '', '', '', r?.created_at, r?.released_at || '']?.map(escapeCSV)?.join(',')));
  data?.signatures?.forEach(r => rows?.push(['Signature Numérique', r?.id, r?.transaction_id || '', '', r?.party_role || '', r?.signer_name || '', r?.signer_email || '', r?.signature_hash || '', r?.signed_at || r?.created_at, '']?.map(escapeCSV)?.join(',')));
  data?.escalations?.forEach(r => rows?.push(['Escalade Litige', r?.id, r?.transaction_ref || '', r?.amount || '', r?.status, r?.title || '', r?.submitted_by_name || '', r?.category || '', r?.created_at, '']?.map(escapeCSV)?.join(',')));
  return '\uFEFF' + rows?.join('\r\n');
};

// ─── Build unified timeline ───────────────────────────────────────────────────

const buildTimeline = (data) => {
  let events = [];

  data?.escrow?.forEach(r => events?.push({ id: r?.id, type: 'escrow', label: r?.title || r?.item_title || 'Transaction', status: r?.status, amount: r?.amount_total || r?.total_amount, ref: r?.reference || r?.paystack_reference, ts: r?.created_at, raw: r }));
  data?.payments?.forEach(r => events?.push({ id: r?.id, type: 'payment', label: 'Paiement', status: r?.status, amount: r?.amount, ref: r?.paystack_reference, ts: r?.created_at, raw: r }));
  data?.disputes?.forEach(r => events?.push({ id: r?.id, type: 'dispute', label: r?.dispute_reason?.slice(0, 40) || 'Litige', status: r?.status, ts: r?.created_at, raw: r }));
  data?.adminActions?.forEach(r => events?.push({ id: r?.id, type: 'admin', label: r?.action_type || 'Action Admin', status: r?.action_type, user: r?.admin_user_id, ts: r?.created_at, raw: r }));
  data?.webhooks?.forEach(r => events?.push({ id: r?.id, type: 'webhook', label: r?.event_type || 'Webhook', status: r?.processed ? 'processed' : 'pending', ref: r?.reference, ts: r?.created_at, raw: r }));
  data?.commissions?.forEach(r => events?.push({ id: r?.id, type: 'commission', label: r?.commission_type || 'Commission', status: r?.status, amount: r?.amount, ts: r?.created_at, raw: r }));
  data?.signatures?.forEach(r => events?.push({ id: r?.id, type: 'signature', label: `Signature — ${r?.party_role || ''}`, status: 'signed', user: r?.signer_name, ts: r?.signed_at || r?.created_at, raw: r }));
  data?.escalations?.forEach(r => events?.push({ id: r?.id, type: 'escalation', label: r?.title || 'Escalade', status: r?.status, user: r?.submitted_by_name, ts: r?.created_at, raw: r }));

  return events?.sort((a, b) => new Date(b?.ts) - new Date(a?.ts));
};

// ─── Type config ──────────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  escrow: { label: 'Séquestre', color: 'text-blue-600', bg: 'bg-blue-100', dot: 'bg-blue-500', icon: Activity },
  payment: { label: 'Paiement', color: 'text-emerald-600', bg: 'bg-emerald-100', dot: 'bg-emerald-500', icon: CheckCircle },
  dispute: { label: 'Litige', color: 'text-red-600', bg: 'bg-red-100', dot: 'bg-red-500', icon: AlertTriangle },
  admin: { label: 'Action Admin', color: 'text-purple-600', bg: 'bg-purple-100', dot: 'bg-purple-500', icon: Shield },
  webhook: { label: 'Webhook', color: 'text-amber-600', bg: 'bg-amber-100', dot: 'bg-amber-500', icon: Clock },
  commission: { label: 'Commission', color: 'text-brand-earth', bg: 'bg-brand-earth/10', dot: 'bg-brand-earth', icon: FileText },
  signature: { label: 'Signature', color: 'text-indigo-600', bg: 'bg-indigo-100', dot: 'bg-indigo-500', icon: FileText },
  escalation: { label: 'Escalade', color: 'text-orange-600', bg: 'bg-orange-100', dot: 'bg-orange-500', icon: AlertTriangle },
};

// ─── Summary Stats ────────────────────────────────────────────────────────────

const SummaryStats = ({ data }) => {
  const stats = [
    { icon: Activity, label: 'Transactions séquestre', value: data?.escrow?.length || 0, color: 'text-blue-600', bg: 'bg-blue-50' },
    { icon: CheckCircle, label: 'Paiements', value: data?.payments?.length || 0, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { icon: AlertTriangle, label: 'Litiges', value: data?.disputes?.length || 0, color: 'text-red-600', bg: 'bg-red-50' },
    { icon: Shield, label: 'Actions admin', value: data?.adminActions?.length || 0, color: 'text-purple-600', bg: 'bg-purple-50' },
    { icon: Clock, label: 'Webhooks', value: data?.webhooks?.length || 0, color: 'text-amber-600', bg: 'bg-amber-50' },
    { icon: FileText, label: 'Commissions', value: data?.commissions?.length || 0, color: 'text-brand-earth', bg: 'bg-brand-earth/10' },
    { icon: FileText, label: 'Signatures', value: data?.signatures?.length || 0, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { icon: AlertTriangle, label: 'Escalades', value: data?.escalations?.length || 0, color: 'text-orange-600', bg: 'bg-orange-50' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
      {stats?.map((s, i) => (
        <div key={i} className="bg-white dark:bg-card rounded-xl border border-border/50 p-3 flex flex-col items-center text-center">
          <div className={`w-8 h-8 rounded-lg ${s?.bg} flex items-center justify-center mb-2`}>
            <s.icon className={`w-4 h-4 ${s?.color}`} />
          </div>
          <p className="text-xl font-bold text-text-primary">{s?.value}</p>
          <p className="text-[10px] text-text-secondary mt-0.5 leading-tight">{s?.label}</p>
        </div>
      ))}
    </div>
  );
};

// ─── Inline Document/Signature Preview ───────────────────────────────────────

const InlinePreviewPanel = ({ item, onClose }) => {
  if (!item) return null;
  const cfg = TYPE_CONFIG?.[item?.type] || TYPE_CONFIG?.admin;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white dark:bg-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-card border-b border-border/40 px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl ${cfg?.bg} flex items-center justify-center`}>
              <cfg.icon className={`w-4 h-4 ${cfg?.color}`} />
            </div>
            <div>
              <p className="text-sm font-bold text-text-primary">{cfg?.label}</p>
              <p className="text-xs text-text-secondary font-mono">{item?.id?.slice(-12)?.toUpperCase()}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {/* Signature-specific preview */}
          {item?.type === 'signature' && (
            <div className="space-y-3">
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
                <p className="text-xs font-bold text-indigo-700 mb-3">Détails de la signature numérique</p>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-text-secondary">Signataire</span><span className="font-semibold text-text-primary">{item?.raw?.signer_name || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-text-secondary">Rôle</span><span className="font-semibold text-text-primary capitalize">{item?.raw?.party_role || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-text-secondary">Email</span><span className="font-mono text-text-primary">{item?.raw?.signer_email || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-text-secondary">Horodatage</span><span className="font-semibold text-indigo-700">{formatDateTime(item?.raw?.signed_at)}</span></div>
                  <div className="flex justify-between"><span className="text-text-secondary">Transaction</span><span className="font-mono text-text-primary">{item?.raw?.transaction_id?.slice(-8)?.toUpperCase() || '—'}</span></div>
                </div>
              </div>
              {item?.raw?.signature_hash && (
                <div className="p-3 bg-surface/50 border border-border/40 rounded-xl">
                  <p className="text-[10px] font-bold text-text-secondary uppercase tracking-wider mb-2">Hash de signature</p>
                  <p className="text-xs font-mono text-text-primary break-all">{item?.raw?.signature_hash}</p>
                </div>
              )}
            </div>
          )}

          {/* Escalation-specific preview */}
          {item?.type === 'escalation' && (
            <div className="space-y-3">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
                <p className="text-xs font-bold text-orange-700 mb-3">Détails de l'escalade</p>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-text-secondary">Titre</span><span className="font-semibold text-text-primary">{item?.raw?.title || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-text-secondary">Catégorie</span><span className="font-semibold text-text-primary">{item?.raw?.category || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-text-secondary">Sévérité</span><span className={`font-bold capitalize ${item?.raw?.severity_level === 'critical' ? 'text-red-600' : item?.raw?.severity_level === 'high' ? 'text-orange-600' : 'text-amber-600'}`}>{item?.raw?.severity_level || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-text-secondary">Soumis par</span><span className="font-semibold text-text-primary">{item?.raw?.submitted_by_name || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-text-secondary">Rôle</span><span className="font-semibold text-text-primary capitalize">{item?.raw?.submitted_by_role || '—'}</span></div>
                  {item?.raw?.amount && <div className="flex justify-between"><span className="text-text-secondary">Montant</span><span className="font-bold text-text-primary">{formatPrice(item?.raw?.amount)}</span></div>}
                </div>
              </div>
            </div>
          )}

          {/* Generic raw data */}
          <div>
            <p className="text-[10px] font-bold text-text-secondary uppercase tracking-wider mb-2">Données brutes</p>
            <pre className="text-[10px] text-text-secondary whitespace-pre-wrap font-mono overflow-x-auto bg-surface/50 p-3 rounded-xl border border-border/40">
              {JSON.stringify(item?.raw, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Interactive Timeline ─────────────────────────────────────────────────────

const InteractiveTimeline = ({ events, onPreview }) => {
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 30;
  const pageEvents = events?.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(events?.length / PAGE_SIZE);

  return (
    <div className="bg-white dark:bg-card rounded-2xl border border-border/50 overflow-hidden">
      <div className="px-5 py-4 border-b border-border/40 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-brand-earth" />
          <p className="text-sm font-bold text-text-primary">Timeline interactive</p>
          <span className="px-2 py-0.5 bg-border/30 rounded-full text-[10px] text-text-secondary">{events?.length} événements</span>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-2 py-1 border border-border/40 rounded-lg disabled:opacity-40 hover:bg-surface/50 transition-colors">‹</button>
            <span>{page + 1}/{totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1} className="px-2 py-1 border border-border/40 rounded-lg disabled:opacity-40 hover:bg-surface/50 transition-colors">›</button>
          </div>
        )}
      </div>

      <div className="divide-y divide-border/20">
        {pageEvents?.map((event, i) => {
          const cfg = TYPE_CONFIG?.[event?.type] || TYPE_CONFIG?.admin;
          return (
            <div
              key={`${event?.id}-${i}`}
              className="flex items-center gap-4 px-5 py-3 hover:bg-surface/30 cursor-pointer transition-colors group"
              onClick={() => onPreview(event)}
            >
              {/* Time column */}
              <div className="w-20 flex-shrink-0 text-right">
                <p className="text-[10px] text-text-secondary/60 font-mono">{formatDateShort(event?.ts)}</p>
                <p className="text-[10px] text-text-secondary/40 font-mono">
                  {event?.ts ? new Date(event?.ts)?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                </p>
              </div>

              {/* Dot + line */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div className={`w-2.5 h-2.5 rounded-full ${cfg?.dot}`} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${cfg?.bg} ${cfg?.color}`}>
                    {cfg?.label}
                  </span>
                  <p className="text-xs font-semibold text-text-primary truncate">{event?.label}</p>
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                  {event?.status && (
                    <span className="text-[10px] text-text-secondary">{event?.status}</span>
                  )}
                  {event?.amount && (
                    <span className="text-[10px] font-semibold text-text-primary">{formatPrice(event?.amount)}</span>
                  )}
                  {event?.user && (
                    <span className="text-[10px] text-text-secondary/60 truncate">{event?.user}</span>
                  )}
                  {event?.ref && (
                    <span className="text-[10px] font-mono text-text-secondary/50 truncate">{event?.ref?.slice(0, 12)}</span>
                  )}
                </div>
              </div>

              {/* Preview button */}
              <button className="opacity-0 group-hover:opacity-100 flex items-center gap-1 px-2 py-1 bg-brand-earth/10 text-brand-earth rounded-lg text-[10px] font-semibold transition-all flex-shrink-0">
                <Eye className="w-3 h-3" />
                Aperçu
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Drill-Down Panel ─────────────────────────────────────────────────────────

const DrillDownPanel = ({ data, onPreview }) => {
  const [drillMode, setDrillMode] = useState('transaction');
  const [search, setSearch] = useState('');

  const transactionGroups = useMemo(() => {
    const groups = {};
    [...(data?.escrow || []), ...(data?.payments || [])]?.forEach(r => {
      const ref = r?.reference || r?.paystack_reference || r?.id?.slice(-8);
      if (!groups?.[ref]) groups[ref] = { ref, items: [], amount: 0 };
      groups?.[ref]?.items?.push(r);
      groups[ref].amount += r?.amount_total || r?.total_amount || r?.amount || 0;
    });
    return Object.values(groups);
  }, [data]);

  const userGroups = useMemo(() => {
    const groups = {};
    [...(data?.adminActions || []), ...(data?.signatures || []), ...(data?.escalations || [])]?.forEach(r => {
      const user = r?.admin_user_id || r?.signer_name || r?.submitted_by_name || 'Inconnu';
      if (!groups?.[user]) groups[user] = { user, items: [], roles: new Set() };
      groups?.[user]?.items?.push(r);
      if (r?.party_role) groups?.[user]?.roles?.add(r?.party_role);
      if (r?.submitted_by_role) groups?.[user]?.roles?.add(r?.submitted_by_role);
    });
    return Object.values(groups);
  }, [data]);

  const actionGroups = useMemo(() => {
    const groups = {};
    [...(data?.adminActions || []), ...(data?.webhooks || [])]?.forEach(r => {
      const action = r?.action_type || r?.event_type || 'unknown';
      if (!groups?.[action]) groups[action] = { action, count: 0, items: [] };
      groups[action].count++;
      groups?.[action]?.items?.push(r);
    });
    return Object.values(groups)?.sort((a, b) => b?.count - a?.count);
  }, [data]);

  const filterFn = (item) => {
    if (!search) return true;
    const s = search?.toLowerCase();
    return JSON.stringify(item)?.toLowerCase()?.includes(s);
  };

  return (
    <div className="bg-white dark:bg-card rounded-2xl border border-border/50 overflow-hidden">
      <div className="px-5 py-4 border-b border-border/40">
        <div className="flex items-center gap-2 mb-3">
          <Layers className="w-4 h-4 text-brand-earth" />
          <p className="text-sm font-bold text-text-primary">Drill-down analytique</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'transaction', label: 'Par transaction', icon: Activity },
            { key: 'user', label: 'Par utilisateur', icon: Users },
            { key: 'action', label: 'Par action', icon: Shield },
          ]?.map(m => (
            <button
              key={m?.key}
              onClick={() => setDrillMode(m?.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${drillMode === m?.key ? 'bg-brand-earth text-white' : 'bg-surface/50 text-text-secondary hover:text-text-primary border border-border/40'}`}
            >
              <m.icon className="w-3 h-3" />
              {m?.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-secondary/50" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            placeholder="Rechercher…"
            className="w-full pl-8 pr-3 py-2 rounded-xl border border-border/60 bg-surface/50 text-xs text-text-primary focus:outline-none focus:border-brand-earth/60 transition-colors"
          />
        </div>

        <div className="space-y-2 max-h-80 overflow-y-auto">
          {drillMode === 'transaction' && transactionGroups?.filter(filterFn)?.map((g, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-2.5 bg-surface/30 border border-border/30 rounded-xl hover:bg-surface/60 transition-colors cursor-pointer" onClick={() => onPreview({ type: 'escrow', label: g?.ref, id: g?.ref, raw: g?.items?.[0] })}>
              <div>
                <p className="text-xs font-semibold text-text-primary font-mono">{g?.ref}</p>
                <p className="text-[10px] text-text-secondary">{g?.items?.length} entrée(s)</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold text-text-primary">{formatPrice(g?.amount)}</p>
                <Eye className="w-3 h-3 text-text-secondary/40 ml-auto mt-0.5" />
              </div>
            </div>
          ))}

          {drillMode === 'user' && userGroups?.filter(filterFn)?.map((g, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-2.5 bg-surface/30 border border-border/30 rounded-xl">
              <div>
                <p className="text-xs font-semibold text-text-primary">{g?.user}</p>
                <p className="text-[10px] text-text-secondary">{g?.items?.length} action(s) · {[...g?.roles]?.join(', ') || 'admin'}</p>
              </div>
              <span className="text-xs font-bold text-brand-earth">{g?.items?.length}</span>
            </div>
          ))}

          {drillMode === 'action' && actionGroups?.filter(filterFn)?.map((g, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-2.5 bg-surface/30 border border-border/30 rounded-xl">
              <p className="text-xs font-semibold text-text-primary">{g?.action}</p>
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-20 bg-border/30 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-earth rounded-full" style={{ width: `${Math.min(100, (g?.count / (actionGroups?.[0]?.count || 1)) * 100)}%` }} />
                </div>
                <span className="text-xs font-bold text-text-primary w-6 text-right">{g?.count}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Role-Based Filter Bar ────────────────────────────────────────────────────

const RoleFilterBar = ({ activeRole, onChange }) => {
  const roles = [
    { value: 'all', label: 'Tous', icon: Shield },
    { value: 'admin', label: 'Admin', icon: Shield },
    { value: 'notary', label: 'Notaire', icon: FileText },
    { value: 'agent', label: 'Agent', icon: Users },
    { value: 'buyer', label: 'Acheteur', icon: Users },
    { value: 'seller', label: 'Vendeur', icon: Users },
  ];

  return (
    <div className="flex flex-wrap gap-2 items-center">
      <span className="text-xs font-semibold text-text-secondary flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5" />
        Filtrer par rôle :
      </span>
      {roles?.map(r => (
        <button
          key={r?.value}
          onClick={() => onChange(r?.value)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeRole === r?.value ? 'bg-brand-earth text-white' : 'bg-surface/50 text-text-secondary hover:text-text-primary border border-border/40'}`}
        >
          <r.icon className="w-3 h-3" />
          {r?.label}
        </button>
      ))}
    </div>
  );
};

// ─── Preview Table ────────────────────────────────────────────────────────────

const PreviewTable = ({ data, onPreview }) => {
  const [section, setSection] = useState('escrow');
  const [expandedRow, setExpandedRow] = useState(null);

  const sections = [
    { key: 'escrow', label: 'Séquestre', count: data?.escrow?.length },
    { key: 'payments', label: 'Paiements', count: data?.payments?.length },
    { key: 'disputes', label: 'Litiges', count: data?.disputes?.length },
    { key: 'adminActions', label: 'Actions Admin', count: data?.adminActions?.length },
    { key: 'webhooks', label: 'Webhooks', count: data?.webhooks?.length },
    { key: 'commissions', label: 'Commissions', count: data?.commissions?.length },
    { key: 'signatures', label: 'Signatures', count: data?.signatures?.length },
    { key: 'escalations', label: 'Escalades', count: data?.escalations?.length },
  ]?.filter(s => data?.[s?.key]?.length > 0);

  const rows = data?.[section] || [];

  return (
    <div className="bg-white dark:bg-card rounded-2xl border border-border/50 overflow-hidden">
      <div className="flex overflow-x-auto border-b border-border/40 px-4 pt-3 gap-1">
        {sections?.map(s => (
          <button
            key={s?.key}
            onClick={() => setSection(s?.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-xs font-semibold whitespace-nowrap transition-colors ${section === s?.key ? 'bg-brand-earth/10 text-brand-earth border-b-2 border-brand-earth' : 'text-text-secondary hover:text-text-primary'}`}
          >
            {s?.label}
            <span className="px-1.5 py-0.5 bg-border/40 rounded-full text-[10px]">{s?.count}</span>
          </button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-muted/30 border-b border-border/40">
              <th className="text-left px-4 py-2.5 text-text-secondary font-semibold">ID</th>
              <th className="text-left px-4 py-2.5 text-text-secondary font-semibold">Statut</th>
              <th className="text-left px-4 py-2.5 text-text-secondary font-semibold">Montant / Détail</th>
              <th className="text-left px-4 py-2.5 text-text-secondary font-semibold">Horodatage</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {rows?.slice(0, 20)?.map((row, i) => {
              const isOpen = expandedRow === row?.id;
              const typeKey = section === 'adminActions' ? 'admin' : section === 'escalations' ? 'escalation' : section?.replace('s', '');
              const cfg = TYPE_CONFIG?.[typeKey] || TYPE_CONFIG?.admin;
              return (
                <React.Fragment key={row?.id || i}>
                  <tr className="border-b border-border/20 hover:bg-surface/40 cursor-pointer" onClick={() => setExpandedRow(isOpen ? null : row?.id)}>
                    <td className="px-4 py-2.5 font-mono text-text-secondary truncate max-w-[100px]">{row?.id?.slice(-8)?.toUpperCase() || '—'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${cfg?.bg} ${cfg?.color}`}>
                        {row?.status || row?.action_type || row?.event_type || row?.party_role || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-text-primary">
                      {row?.amount || row?.amount_total || row?.total_amount
                        ? formatPrice(row?.amount || row?.amount_total || row?.total_amount)
                        : row?.dispute_reason?.slice(0, 40) || row?.notes?.slice(0, 40) || row?.title?.slice(0, 40) || row?.reference || row?.signer_name || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-text-secondary whitespace-nowrap">{formatDateTime(row?.created_at || row?.signed_at)}</td>
                    <td className="px-4 py-2.5 text-text-secondary">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e?.stopPropagation(); onPreview({ type: typeKey, label: row?.title || row?.action_type || row?.event_type || row?.id?.slice(-8), id: row?.id, raw: row }); }}
                          className="flex items-center gap-1 px-2 py-0.5 bg-brand-earth/10 text-brand-earth rounded text-[10px] font-semibold hover:bg-brand-earth/20 transition-colors"
                        >
                          <Eye className="w-3 h-3" />
                          Aperçu
                        </button>
                        {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-surface/30">
                      <td colSpan={5} className="px-4 py-3">
                        <pre className="text-[10px] text-text-secondary whitespace-pre-wrap font-mono overflow-x-auto">
                          {JSON.stringify(row, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {rows?.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-text-secondary text-xs">Aucune donnée dans cette catégorie</td></tr>
            )}
          </tbody>
        </table>
        {rows?.length > 20 && (
          <p className="text-center text-[10px] text-text-secondary py-2 border-t border-border/30">
            Affichage des 20 premiers sur {rows?.length} — téléchargez le CSV pour l'intégralité
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Main ComplianceAuditReport ───────────────────────────────────────────────

const ComplianceAuditReport = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('timeline');
  const [previewItem, setPreviewItem] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', types: ['all'] });

  const TYPE_OPTIONS = [
    { value: 'all', label: 'Tout inclure' },
    { value: 'transactions', label: 'Transactions' },
    { value: 'disputes', label: 'Litiges & Escalades' },
    { value: 'admin_actions', label: 'Actions Admin' },
    { value: 'webhooks', label: 'Webhooks' },
    { value: 'commissions', label: 'Commissions' },
    { value: 'signatures', label: 'Signatures numériques' },
  ];

  const handleGenerate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAuditData(filters);
      setData(result);
    } catch (err) {
      setError('Erreur lors de la génération du rapport : ' + err?.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleDownloadCSV = useCallback(() => {
    if (!data) return;
    const csv = buildCSV(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date()?.toISOString()?.slice(0, 10);
    a.download = `rapport-conformite-kapitalestates-${ts}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  }, [data]);

  const timeline = useMemo(() => {
    if (!data) return [];
    let events = buildTimeline(data);
    if (roleFilter !== 'all') {
      events = events?.filter(e => {
        const raw = e?.raw;
        return raw?.party_role === roleFilter || raw?.submitted_by_role === roleFilter || (roleFilter === 'admin' && (raw?.admin_user_id || e?.type === 'admin'));
      });
    }
    return events;
  }, [data, roleFilter]);

  const totalRecords = data
    ? (data?.escrow?.length || 0) + (data?.payments?.length || 0) + (data?.disputes?.length || 0) +
      (data?.adminActions?.length || 0) + (data?.webhooks?.length || 0) + (data?.commissions?.length || 0) +
      (data?.signatures?.length || 0) + (data?.escalations?.length || 0)
    : 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-text-primary flex items-center gap-2">
            <Shield className="w-5 h-5 text-brand-earth" />
            Rapport d'audit de conformité
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Timeline interactive, drill-down analytique, filtrage par rôle et prévisualisation inline des signatures et documents.
          </p>
        </div>
        {data && (
          <button
            onClick={handleDownloadCSV}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-earth text-white rounded-xl text-sm font-semibold hover:bg-brand-earth/90 transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            Télécharger CSV ({totalRecords} entrées)
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-card rounded-2xl border border-border/50 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-brand-earth" />
          <p className="text-sm font-semibold text-text-primary">Filtres du rapport</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">Date de début</label>
            <input type="date" value={filters?.dateFrom} onChange={e => setFilters(p => ({ ...p, dateFrom: e?.target?.value }))} className="w-full px-3 py-2 rounded-xl border border-border/60 bg-surface/50 text-sm text-text-primary focus:outline-none focus:border-brand-earth/60 transition-colors" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">Date de fin</label>
            <input type="date" value={filters?.dateTo} onChange={e => setFilters(p => ({ ...p, dateTo: e?.target?.value }))} className="w-full px-3 py-2 rounded-xl border border-border/60 bg-surface/50 text-sm text-text-primary focus:outline-none focus:border-brand-earth/60 transition-colors" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">Types de données</label>
            <select value={filters?.types?.[0]} onChange={e => setFilters(p => ({ ...p, types: [e?.target?.value] }))} className="w-full px-3 py-2 rounded-xl border border-border/60 bg-surface/50 text-sm text-text-primary focus:outline-none focus:border-brand-earth/60 transition-colors">
              {TYPE_OPTIONS?.map(opt => <option key={opt?.value} value={opt?.value}>{opt?.label}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button onClick={handleGenerate} disabled={loading} className="flex items-center gap-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-sm font-semibold hover:bg-brand-earth/90 disabled:opacity-60 transition-colors">
            {loading ? <><RefreshCw className="w-4 h-4 animate-spin" />Génération…</> : <><FileText className="w-4 h-4" />Générer le rapport</>}
          </button>
        </div>
        {error && (
          <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}
      </div>

      {/* Results */}
      {data && (
        <>
          <SummaryStats data={data} />

          {/* Role filter */}
          <div className="bg-white dark:bg-card rounded-2xl border border-border/50 px-5 py-4">
            <RoleFilterBar activeRole={roleFilter} onChange={setRoleFilter} />
          </div>

          {/* View switcher */}
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'timeline', label: 'Timeline', icon: Calendar },
              { key: 'drilldown', label: 'Drill-down', icon: Layers },
              { key: 'table', label: 'Tableau', icon: FileText },
            ]?.map(v => (
              <button
                key={v?.key}
                onClick={() => setActiveView(v?.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${activeView === v?.key ? 'bg-brand-earth text-white shadow-sm' : 'bg-white dark:bg-card border border-border/50 text-text-secondary hover:text-text-primary'}`}
              >
                <v.icon className="w-4 h-4" />
                {v?.label}
              </button>
            ))}
            <div className="ml-auto">
              <button onClick={handleDownloadCSV} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-card border border-border/50 rounded-xl text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors">
                <Download className="w-4 h-4" />
                CSV
              </button>
            </div>
          </div>

          {activeView === 'timeline' && (
            <InteractiveTimeline events={timeline} onPreview={setPreviewItem} />
          )}

          {activeView === 'drilldown' && (
            <DrillDownPanel data={data} onPreview={setPreviewItem} />
          )}

          {activeView === 'table' && (
            <PreviewTable data={data} onPreview={setPreviewItem} />
          )}
        </>
      )}

      {/* Inline preview panel */}
      {previewItem && (
        <InlinePreviewPanel item={previewItem} onClose={() => setPreviewItem(null)} />
      )}
    </div>
  );
};

export default ComplianceAuditReport;
